<script setup>
import {onMounted, ref} from 'vue';
import {Capacitor} from '@capacitor/core';
import {authenticateWithBiometric, getAvailableTypeString} from '@/services/BiometricService';
import {getTenantData} from '@/services/TenantService';
import {BiometricAuth} from '@aparajita/capacitor-biometric-auth';
import {setButtonContentLoading, setButtonContentSuccess} from '@/helper';
import Textarea from '@/components/forms/Textarea.vue';
import Input from '@/components/forms/Input.vue';

const props = defineProps({
  config: {type: Object, required: false},
  prefillObject: {type: Object, required: false},
  stepIndex: {type: Number, required: false, default: null},
  disabled: {type: Boolean, default: false},
});

// chybí options pro select a radio
const config = ref([]);
const duplicationIndex = ref(0);

defineExpose({config});

function checkCondition(question, fieldset) {
  if (!question.visibleWhen) {
    return true;
  }

  let returnVal = false;
  const condition = question.visibleWhen;
  const field = fieldset.questions.find(q => q.id === condition.field);
  if (!field) {
    return false;
  }

  if (condition.operator === 'in' && typeof condition.value === 'object') {
    const availableValues = Object.values(condition.value);
    returnVal = availableValues.includes(field.value);
  } else {
    switch (condition.operator) {
      case '==':
        returnVal = field.value == condition.value;
        break;
      case '>':
        returnVal = field.value > condition.value;
        break;
      case '<':
        returnVal = field.value < condition.value;
        break;
      case '>=':
        returnVal = field.value >= condition.value;
        break;
      case '<=':
        returnVal = field.value <= condition.value;
        break;
      case '!=':
        returnVal = field.value != condition.value;
        break;
      default:
        returnVal = false;
    }
  }

  return returnVal;
}

function duplicateSection(object, where, index) {
  const newObject = {
    ...object,
    duplication: true,
    questions: object.questions.map(q => ({...q, id: q.id + `--${duplicationIndex.value}`, value: null}))
  };

  where.splice(index, 0, newObject);
  duplicationIndex.value++;
}

function removeDuplicateSection(where, index) {
  where.splice(index, 1);
}

onMounted(async () => {
  if (props.config) {
    config.value = props.config;
  } else {
    config.value = defaultConfig;
  }
});

</script>
<template>
  <div class="row h-100 justify-content-center align-items-center">
    <div class="col-12">
      <template v-for="(fieldset, index) of config.content">
        <fieldset v-if="stepIndex === null || (stepIndex === index)" :disabled="props.disabled">
          <legend v-if="fieldset.fieldsetName">{{ fieldset.fieldsetName }}</legend>
          <p v-if="fieldset.fieldsetText">{{ fieldset.fieldsetText }}</p>
          <div v-if="fieldset.fieldsetHtml" v-html="fieldset.fieldsetHtml"></div>
          <small v-if="fieldset.fieldsetDescription">{{ fieldset.fieldsetDescription }}</small>
          <div v-for="(question, index) of fieldset.questions" class="row">
            <div v-if="checkCondition(question, fieldset)" class="col-12 form-group mb-3">
              <label v-if="question.type !== 'checkbox'" :for="question.id" :class="question.isRequired ? 'required-label' : ''" class="form-label">{{ question.label }}</label>
              <p><small>{{ question.description }}</small></p>
              <p v-if="question.text">{{ question.text }}</p>
              <div v-if="question.html" v-html="question.html"></div>

              <input v-if="question.type === 'text'" v-model="question.value" :required="question.isRequired" :id="question.id" class="form-control" type="text"/>
              <textarea v-if="question.type === 'textarea'" v-model="question.value" :required="question.isRequired" :id="question.id" class="form-control" rows="3"></textarea>

              <div v-if="question.type === 'checkbox'" class="form-check">
                <input v-model="question.value" :required="question.isRequired" :id="question.id" class="form-check-input" type="checkbox"/>
                <label :for="question.id" :class="question.isRequired ? 'required-label' : ''" class="form-check-label">{{ question.label }}</label>
              </div>

              <div v-if="question.type === 'radio'">
                <template v-for="(option, index) of question.options">
                  <div class="form-check">
                    <input v-model="question.value" :required="question.isRequired" :id="question.id + '-' + index" :name="question.id" class="form-check-input" type="radio" :value="option.value"/>
                    <label :for="question.id + '-' + index" class="form-check-label">{{ option.label }}</label>
                  </div>
                </template>
              </div>

              <select v-if="question.type === 'select'" v-model="question.value" :required="question.isRequired" :id="question.id" class="form-select">
                <option v-for="option of question.options" :value="option.value">{{ option.label }}</option>
              </select>
              <input v-if="question.type === 'number'" v-model="question.value" :required="question.isRequired" :id="question.id" class="form-control" type="number"/>
              <input v-if="question.type === 'date'" v-model="question.value" :required="question.isRequired" :id="question.id" class="form-control" type="date"/>
              <input v-if="question.type === 'datetime'" v-model="question.value" :required="question.isRequired" :id="question.id" class="form-control" type="datetime-local"/>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 text-end" v-if="fieldset.fieldsetRepeatable">
              <button v-if="fieldset.duplication" type="button" @click="removeDuplicateSection(config.content, index)" class="btn btn-danger me-2">Smazat sekci</button>
              <button type="button" @click="duplicateSection(fieldset, config.content, index + 1)" class="btn btn-primary">Přidat sekci</button>
            </div>
          </div>
        </fieldset>
      </template>
    </div>
  </div>
</template>

<style scoped>
/* Potlačení globálního border stylu fieldsetů — DynamicForm nemá vizuální ohraničení sekcí */
fieldset {
  border: none;
  padding: 0;
  margin: 0;
}
</style>

